import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { Contract, Customer, Supervisor, Controller } from '../types';
import ContractAssignment from '../components/ContractAssignment';
import FilterToggle from '../components/FilterToggle';

const ContractAssignments: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'supervised' | 'unsupervised'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsRes, customersRes, supervisorsRes, controllersRes] = await Promise.all([
        apiService.getContracts(1, 100), // Get more contracts for assignment view
        apiService.getCustomers(1, 100),
        apiService.getAllSupervisors(),
        apiService.getAllControllers()
      ]);

      // Only show active contracts
      const activeContracts = contractsRes.items.filter((c: Contract) => c.isactive && !c.isdeleted);
      
      setContracts(activeContracts);
      setCustomers(customersRes.items);
      setSupervisors(supervisorsRes.filter((s: Supervisor) => s.is_active));
      setControllers(controllersRes.filter((c: Controller) => c.is_active));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerForContract = (contract: Contract): Customer | null => {
    return customers.find((c: Customer) => c.id === contract.customer_id) || null;
  };

  const getAssignmentStatus = (customer: Customer | null) => {
    if (!customer) return 'no-customer';
    if (customer.supervisor_id || customer.controller_id) return 'supervised';
    return 'unsupervised';
  };

  const filteredContracts = contracts.filter(contract => {
    const customer = getCustomerForContract(contract);
    const status = getAssignmentStatus(customer);

    // Apply filter
    if (filterType === 'supervised' && status !== 'supervised') return false;
    if (filterType === 'unsupervised' && status !== 'unsupervised') return false;

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesContract = contract.contract_name.toLowerCase().includes(searchLower);
      const matchesCustomer = customer?.customer_name.toLowerCase().includes(searchLower);
      
      if (customer?.supervisor_id) {
        const supervisor = supervisors.find(s => s.id === customer.supervisor_id);
        const matchesSupervisor = supervisor?.display_name?.toLowerCase().includes(searchLower);
        return matchesContract || matchesCustomer || matchesSupervisor;
      }
      
      if (customer?.controller_id) {
        const controller = controllers.find((c: Controller) => c.id === customer.controller_id);
        const matchesController = controller?.controller_name.toLowerCase().includes(searchLower);
        return matchesContract || matchesCustomer || matchesController;
      }
      
      return matchesContract || matchesCustomer;
    }

    return true;
  });

  const stats = {
    total: contracts.length,
    supervised: contracts.filter((c: Contract) => {
      const customer = getCustomerForContract(c);
      return customer && (customer.supervisor_id || customer.controller_id);
    }).length,
    unsupervised: contracts.filter((c: Contract) => {
      const customer = getCustomerForContract(c);
      return customer && !customer.supervisor_id && !customer.controller_id;
    }).length,
    noCustomer: contracts.filter((c: Contract) => !getCustomerForContract(c)).length
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Contract Assignments</h1>
      </div>

      {/* Statistics */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Contracts</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.supervised}</div>
          <div className="stat-label">Supervised</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.unsupervised}</div>
          <div className="stat-label">Not Supervised</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{stats.noCustomer}</div>
          <div className="stat-label">No Customer</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <FilterToggle
              value={filterType}
              onChange={setFilterType}
              options={[
                { value: 'all', label: 'All Contracts' },
                { value: 'supervised', label: 'Supervised' },
                { value: 'unsupervised', label: 'Not Supervised' }
              ]}
            />
            <div className="form-group" style={{ margin: 0, flex: 1, maxWidth: '300px' }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search contracts, customers, supervisors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Contract</th>
                <th>Customer</th>
                <th>Current Assignment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContracts.length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '40px' }}>
                    No contracts found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredContracts.map((contract) => {
                  const customer = getCustomerForContract(contract);
                  return (
                    <tr key={contract.contract_id}>
                      <td>
                        <strong>{contract.contract_name}</strong>
                        <br />
                        <small className="text-muted">#{contract.contract_number}</small>
                      </td>
                      <td>
                        {customer ? (
                          <>
                            {customer.customer_name}
                            <br />
                            <small className="text-muted">{customer.customer_type}</small>
                          </>
                        ) : (
                          <span className="text-muted">No customer assigned</span>
                        )}
                      </td>
                      <td>
                        {customer ? (
                          <ContractAssignment
                            contract={contract}
                            onUpdate={fetchData}
                          />
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <a
                          href={`/contracts/${contract.contract_id}`}
                          className="btn btn-outline btn-sm"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Contract
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ContractAssignments;